import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVehicleImage } from "@/hooks/useVehicleImage";
import defaultVehicleImage from "@/assets/xxx.png";

interface VehicleHeroImageProps {
  /** User-uploaded exterior photo (highest priority when present). */
  exteriorPhoto?: string;
  make?: string;
  model?: string;
  alt: string;
  /** Tailwind height utility for the image/loader, e.g. "h-44". */
  heightClass?: string;
}

/**
 * Vehicle image with a 3-level fallback: exterior photo → CDN-resolved image
 * (by make/model) → default duri placeholder. The CDN url is pre-validated by
 * `useVehicleImage`; the exterior photo advances on error via the ordered list.
 */
export function VehicleHeroImage({
  exteriorPhoto,
  make,
  model,
  alt,
  heightClass = "h-44",
}: VehicleHeroImageProps) {
  const { data: cdnUrl, isLoading, isFetching } = useVehicleImage(make, model);

  const candidates = useMemo(() => {
    const list: string[] = [];
    if (exteriorPhoto?.trim()) list.push(exteriorPhoto.trim());
    if (cdnUrl) list.push(cdnUrl);
    return list;
  }, [exteriorPhoto, cdnUrl]);

  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [candidates]);

  const isResolving = (isLoading || isFetching) && candidates.length === 0;
  const currentSrc = index < candidates.length ? candidates[index] : null;

  if (isResolving) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center bg-muted",
          heightClass
        )}
      >
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <img
      key={currentSrc ?? "default"}
      src={currentSrc ?? defaultVehicleImage}
      alt={alt}
      loading="lazy"
      onError={() => setIndex((prev) => prev + 1)}
      className={cn("w-full bg-muted object-contain", heightClass)}
    />
  );
}
